import type {
	CheckNameResponse,
	CreateCompetencyBody,
	ListCompetenciesResponseJSON,
	SingleCompetencyResponseJSON,
	UpdateCompetencyBody,
} from "@performyard/service-contracts";

import request from "./internal-api/internal-base-request";

// Re-export types used by hooks and components
export type {
	Competency,
	ListCompetenciesResponseJSON,
	SingleCompetencyResponseJSON,
} from "@performyard/service-contracts";

export type CompetencySortBy = "name" | "status" | "levels_count";

export interface ListCompetenciesParams {
	name?: string;
	status?: "active" | "archived" | "any";
	companyWide?: boolean;
	sortBy?: CompetencySortBy;
	sortOrder?: "asc" | "desc";
	limit?: number;
	skip?: number;
}

// GET /v1/competencies
export const getCompetencies = async (params: ListCompetenciesParams = {}): Promise<ListCompetenciesResponseJSON> => {
	const searchParams = new URLSearchParams();
	if (params.name) searchParams.set("name", params.name);
	if (params.status) searchParams.set("status", params.status);
	if (params.companyWide !== undefined) searchParams.set("companyWide", String(params.companyWide));
	if (params.sortBy) searchParams.set("sortBy", params.sortBy);
	if (params.sortOrder) searchParams.set("sortOrder", params.sortOrder);
	if (params.limit !== undefined) searchParams.set("limit", String(params.limit));
	if (params.skip !== undefined) searchParams.set("skip", String(params.skip));

	const query = searchParams.toString();
	const [response] = await request<ListCompetenciesResponseJSON>(`/v1/competencies${query ? `?${query}` : ""}`, {
		method: "GET",
	});
	return response;
};

// GET /v1/competencies/:id
export const getCompetency = async (id: string): Promise<SingleCompetencyResponseJSON> => {
	const [response] = await request<SingleCompetencyResponseJSON>(`/v1/competencies/${id}`, {
		method: "GET",
	});
	return response;
};

// POST /v1/competencies
export const createCompetency = async (body: CreateCompetencyBody): Promise<SingleCompetencyResponseJSON> => {
	const [response] = await request<SingleCompetencyResponseJSON>("/v1/competencies", {
		method: "POST",
		body: JSON.stringify(body),
	});
	return response;
};

// PUT /v1/competencies/:id
export const updateCompetency = async (
	id: string,
	body: UpdateCompetencyBody
): Promise<SingleCompetencyResponseJSON> => {
	const [response] = await request<SingleCompetencyResponseJSON>(`/v1/competencies/${id}`, {
		method: "PUT",
		body: JSON.stringify(body),
	});
	return response;
};

// PATCH /v1/competencies/:id/archive
export const archiveCompetency = async (id: string): Promise<{ success: boolean }> => {
	const [response] = await request<{ success: boolean }>(`/v1/competencies/${id}/archive`, {
		method: "PATCH",
	});
	return response;
};

// PATCH /v1/competencies/:id/reopen
export const reopenCompetency = async (id: string): Promise<{ success: boolean }> => {
	const [response] = await request<{ success: boolean }>(`/v1/competencies/${id}/reopen`, {
		method: "PATCH",
	});
	return response;
};

// GET /v1/competencies/check-name
export const checkCompetencyName = async (name: string, excludeId?: string): Promise<CheckNameResponse> => {
	const searchParams = new URLSearchParams({ name });
	if (excludeId) searchParams.set("excludeId", excludeId);

	const [response] = await request<CheckNameResponse>(`/v1/competencies/check-name?${searchParams.toString()}`, {
		method: "GET",
	});
	return response;
};
